import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import leadService from '../../../api/crm/leads/leadService';
import useUrlTableState from '../../../hooks/useUrlTableState';
// import OpportunityEditModal from '../opportunity-pipeline/components/OpportunityEditModal';
import {
  ArrowRight,
  ArrowLeft,
  Target,
  Calendar,
  User,
  Building,
  MapPin,
  Phone,
  Mail,
  FileText,
  Save,
  X,
  CheckCircle,
  AlertCircle,
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Eye,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  Download,
  Clock
} from 'lucide-react';
// import Button from '../../../components/common/Button/Button';
import ExportModal from '../../../components/common/ExportModal';
import { exportToCSV, downloadFromBackend, generateExportFilename } from '../../../utils/exportUtils';
import { formatDateShort } from '../../../utils/stringUtils';
import { useAuth } from '../../../context/AuthContext';
import { USER_ROLES } from '../../../utils/constants';
import {
  sanitizeTextInput,
  sanitizeSearchInput,
  validateNumberInput,
  handleTextInputChange,
  handleSearchInputChange,
  handleNumberInputChange,
  getCharacterCount,
  FIELD_LIMITS,
  NUMBER_LIMITS
} from '../../../utils/inputValidation';
import PermissionGate from '../../../components/common/PermissionGate';
import { truncateText } from '../../../utils/stringUtils';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import ConvertLeadModal from './ConvertLeadModal';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
// import { useLeadStatuses } from '../../../hooks/useLeadQueries';

import { leadSourceService } from '../../../api/crm/config/configService';

// Map backend source to frontend display (directly populate from backend resolved values)
const mapSourceToFrontend = (source) => {
  if (!source) return 'Manual Entry';
  if (typeof source === 'object') {
    return source.name || source.label || source.id || 'Manual Entry';
  }
  return source;
};

// Map frontend source to backend source (directly map dynamically)
const mapSourceToBackend = (source) => {
  if (!source) return 'manual_entry';
  return typeof source === 'string' ? source.toLowerCase().replace(/\s+/g, '_') : source;
};

const ConvertOpportunity = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get tab from URL or default to 'convert'
  const [activeTab, setActiveTabState] = useState(() => searchParams.get('tab') || 'convert');
  
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  };
  
  // Sync tab state with URL on mount and when URL changes
  useEffect(() => {
    const urlTab = searchParams.get('tab') || 'convert';
    if (urlTab !== activeTab) {
      setActiveTabState(urlTab);
    }
  }, [searchParams]);

  // Default filters for qualified leads tab
  const defaultLeadsFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: location.state?.filters?.search || '',
    source: location.state?.filters?.source || 'all',
    temperature: location.state?.filters?.temperature || 'all',
    startDate: location.state?.filters?.startDate || '',
    endDate: location.state?.filters?.endDate || '',
    sortBy: location.state?.filters?.sortBy || 'lastActivity',
    sortOrder: location.state?.filters?.sortOrder || 'desc'
  }), [location.state]);

  // Default filters for converted opportunities tab
  const defaultOppsFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    oppSearch: location.state?.filters?.oppSearch || '',
    status: location.state?.filters?.status || 'all',
    startDate: location.state?.filters?.startDate || '',
    endDate: location.state?.filters?.endDate || '',
    sortBy: location.state?.filters?.sortBy || 'convertedAtDate',
    sortOrder: location.state?.filters?.sortOrder || 'desc'
  }), [location.state]);

  // Use URL table state for qualified leads
  const {
    filters: leadsFilters,
    searchTerm,
    setSearchTerm: setLeadsSearchTerm,
    updateFilter: updateLeadsFilter,
    updatePage: updateLeadsPage,
    updateLimit: updateLeadsLimit,
    updateSort: updateLeadsSort,
    resetFilters: resetLeadsFilters
  } = useUrlTableState(defaultLeadsFilters, { searchKey: 'search' });

  // Use URL table state for converted opportunities
  const {
    filters: oppsFilters,
    searchTerm: oppsSearchTerm,
    setSearchTerm: setOppsSearchTerm,
    updateFilter: updateOppsFilter,
    updatePage: updateOppsPage,
    updateLimit: updateOppsLimit,
    updateSort: updateOppsSort,
    resetFilters: resetOppsFilters
  } = useUrlTableState(defaultOppsFilters, { searchKey: 'oppSearch' });
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [notification, setNotification] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [leadsForConversion, setLeadsForConversion] = useState([]);
  const [convertedOpportunities, setConvertedOpportunities] = useState([]);
  const [loadingOpportunities, setLoadingOpportunities] = useState(true);
  const [editFormErrors, setEditFormErrors] = useState({});
  const [opportunityPagination, setOpportunityPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  });
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [opportunityToDelete, setOpportunityToDelete] = useState(null);
  const [leadsPagination, setLeadsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  });

  const { user } = useAuth();
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Date Range Filters - derived from URL state
  const qualifiedDateRange = useMemo(() => ({
    startDate: leadsFilters.startDate || '',
    endDate: leadsFilters.endDate || ''
  }), [leadsFilters.startDate, leadsFilters.endDate]);

  const convertedDateRange = useMemo(() => ({
    startDate: oppsFilters.startDate || '',
    endDate: oppsFilters.endDate || ''
  }), [oppsFilters.startDate, oppsFilters.endDate]);

  // Lead Sources from config
  const [leadSources, setLeadSources] = useState([]);

  // Fetch lead statuses (Hardcoded as per requirement)
  const leadStatuses = [
    { value: 'converted', name: 'Converted', isActive: true },
    { value: 'closed_won', name: 'Won', isActive: true },
    { value: 'closed_lost', name: 'Lost', isActive: true }
  ];

  useEffect(() => {
    const fetchLeadSources = async () => {
      try {
        const response = await leadSourceService.getAll({ isActive: true });
        if (response?.success && Array.isArray(response.data)) {
          setLeadSources(response.data);
        }
      } catch (error) {

      }
    };
    fetchLeadSources();
  }, []);

  // Clear selected rows when tab changes
  useEffect(() => {
    setSelectedRows(new Set());
  }, [activeTab]);

  // Total counts for tabs (independent of filters)
  const [totalQualifiedCount, setTotalQualifiedCount] = useState(0);
  const [totalConvertedCount, setTotalConvertedCount] = useState(0);

  // Sorting state - derived from URL state
  const leadsSortColumn = leadsFilters.sortBy;
  const leadsSortDirection = leadsFilters.sortOrder;
  const oppsSortColumn = oppsFilters.sortBy;
  const oppsSortDirection = oppsFilters.sortOrder;

  // Process lost reasons for the selected opportunity
  const opportunityLostReasons = useMemo(() => {
    const leadData = selectedOpportunity?.leadId;
    if (Array.isArray(leadData?.lostReason)) {
      // Sort by date descending
      const sorted = [...leadData.lostReason].sort((a, b) => {
        const dateA = a && typeof a === 'object' ? new Date(a.date || 0) : new Date(0);
        const dateB = b && typeof b === 'object' ? new Date(b.date || 0) : new Date(0);
        return dateB - dateA;
      });

      return sorted.filter(lr => {
        if (!lr) return false;
        if (typeof lr === 'string') return true;
        if (lr.reason) return true;
        // Check for broken indexed objects
        return Object.keys(lr).some(k => !isNaN(parseInt(k)));
      });
    }
    return [];
  }, [selectedOpportunity]);

  // Sorting handlers
  const handleLeadsSort = (column) => {
    if (leadsSortColumn === column) {
      updateLeadsSort(column, leadsSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      updateLeadsSort(column, 'asc');
    }
  };

  const handleOppsSort = (column) => {
    if (oppsSortColumn === column) {
      updateOppsSort(column, oppsSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      updateOppsSort(column, 'asc');
    }
  };

  const getSortedLeadsForConversion = () => {
    if (!leadsSortColumn || !leadsForConversion || leadsForConversion.length === 0) return leadsForConversion;

    return [...leadsForConversion].sort((a, b) => {
      let aValue, bValue;

      if (leadsSortColumn === 'leadId') {
        aValue = a.leadId || '';
        bValue = b.leadId || '';
      } else if (leadsSortColumn === 'leadDetails') {
        aValue = (a.name || '').toLowerCase();
        bValue = (b.name || '').toLowerCase();
      } else if (leadsSortColumn === 'source') {
        aValue = (a.source || '').toLowerCase();
        bValue = (b.source || '').toLowerCase();
      } else if (leadsSortColumn === 'score') {
        aValue = Number(a.score) || 0;
        bValue = Number(b.score) || 0;
      } else if (leadsSortColumn === 'temperature') {
        aValue = (a.scoreLabel || '').toLowerCase();
        bValue = (b.scoreLabel || '').toLowerCase();
      } else if (leadsSortColumn === 'status') {
        aValue = (a.status || '').toLowerCase();
        bValue = (b.status || '').toLowerCase();
      } else if (leadsSortColumn === 'owner') {
        aValue = (a.assignee || a.owner || '').toLowerCase();
        bValue = (b.assignee || b.owner || '').toLowerCase();
      } else if (leadsSortColumn === 'lastActivity') {
        // Use the raw date if available, otherwise fallback
        aValue = new Date(a.lastActivity?.date || a.lastActivity || 0).getTime();
        bValue = new Date(b.lastActivity?.date || b.lastActivity || 0).getTime();
      } else if (leadsSortColumn === 'createdAt') {
        aValue = new Date(a.createdAt || 0).getTime();
        bValue = new Date(b.createdAt || 0).getTime();
      } else {
        aValue = a[leadsSortColumn] || '';
        bValue = b[leadsSortColumn] || '';
      }

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return leadsSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return leadsSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getSortedConvertedOpportunities = () => {
    if (!oppsSortColumn || !convertedOpportunities || convertedOpportunities.length === 0) return convertedOpportunities;

    return [...convertedOpportunities].sort((a, b) => {
      let aValue, bValue;

      if (oppsSortColumn === 'opportunity') {
        aValue = (a.opportunityName || '').toLowerCase();
        bValue = (b.opportunityName || '').toLowerCase();
      } else if (oppsSortColumn === 'leadDetails') {
        aValue = (a.leadName || '').toLowerCase();
        bValue = (b.leadName || '').toLowerCase();
      } else if (oppsSortColumn === 'value') {
        aValue = Number(a.value) || 0;
        bValue = Number(b.value) || 0;
      } else if (oppsSortColumn === 'status') {
        aValue = (a.stage || '').toLowerCase();
        bValue = (b.stage || '').toLowerCase();
      } else if (oppsSortColumn === 'expectedClose') {
        aValue = new Date(a.expectedCloseDate || 0).getTime();
        bValue = new Date(b.expectedCloseDate || 0).getTime();
      } else if (oppsSortColumn === 'convertedAtDate') {
        aValue = new Date(a.convertedAtDate || 0).getTime();
        bValue = new Date(b.convertedAtDate || 0).getTime();
      } else if (oppsSortColumn === 'createdAt') {
        aValue = new Date(a.createdAt || 0).getTime();
        bValue = new Date(b.createdAt || 0).getTime();
      } else if (oppsSortColumn === 'owner') {
        aValue = (a.owner || '').toLowerCase();
        bValue = (b.owner || '').toLowerCase();
      } else if (oppsSortColumn === 'probability') {
        aValue = Number(a.probability) || 0;
        bValue = Number(b.probability) || 0;
      } else {
        aValue = a[oppsSortColumn] || '';
        bValue = b[oppsSortColumn] || '';
      }

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return oppsSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return oppsSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };



  // Helper function to get lead name
  const getLeadName = (lead) => {
    if (lead.firstName && lead.lastName) {
      return `${lead.firstName} ${lead.lastName}`;
    } else if (lead.firstName) {
      return lead.firstName;
    } else if (lead.lastName) {
      return lead.lastName;
    } else {
      return 'Unknown Lead';
    }
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return { date: 'N/A', time: '' };
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    const formattedTime = date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    return { date: formattedDate, time: formattedTime };
  };

  // Handle row selection
  const handleSelectRow = (id) => {
    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return newSelection;
    });
  };

  const handleSelectAll = (items) => {
    if (items.length === 0) {
      setSelectedRows(new Set());
      return;
    }

    // Check if all items on current page are already selected
    const allSelected = items.every(item => selectedRows.has(item._id || item.id));

    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (allSelected) {
        // Deselect all items on current page
        items.forEach(item => newSelection.delete(item._id || item.id));
      } else {
        // Select all items on current page
        items.forEach(item => newSelection.add(item._id || item.id));
      }
      return newSelection;
    });
  };


  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      handleExportCurrentPage();
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportCurrentPage = () => {
    try {
      const dataToExport = activeTab === 'convert' ? leadsForConversion : convertedOpportunities;
      const filteredData = selectedRows.size > 0
        ? dataToExport.filter(item => selectedRows.has(item._id || item.id))
        : dataToExport;

      const pageName = activeTab === 'convert' ? 'qualified_leads' : 'converted_opportunities';
      const title = activeTab === 'convert' ? 'Qualified Leads' : 'Converted Opportunities';
      const type = selectedRows.size > 0 ? 'Selected Items' : 'Current Page';

      let headers, formattedData;

      if (activeTab === 'convert') {
        headers = ['Lead ID', 'Name', 'Company', 'Email', 'Phone', 'Source', 'Score', 'Temperature', 'Status', 'Owner', 'Last Activity'];
        formattedData = filteredData.map(item => ({
          'Lead ID': item.leadId || '',
          'Name': item.name || '',
          'Company': item.company || '',
          'Email': item.email || '',
          'Phone': item.phone || '',
          'Source': item.source || '',
          'Score': item.score || 0,
          'Temperature': item.scoreLabel || '',
          'Status': item.status || '',
          'Owner': item.owner || 'Unassigned',
          'Last Activity': item.lastActivity?.date || ''
        }));
      } else {
        headers = ['Opportunity ID', 'Name', 'Lead Name', 'Company', 'Value', 'Stage', 'Probability', 'Expected Close', 'Converted At', 'Owner'];
        formattedData = filteredData.map(item => ({
          'Opportunity ID': item.opportunityId || '',
          'Name': item.opportunityName || '',
          'Lead Name': item.leadName || '',
          'Company': item.company || '',
          'Value': item.value || 0,
          'Stage': item.stage || '',
          'Probability': `${item.probability || 0}%`,
          'Expected Close': item.expectedClose || '',
          'Converted At': item.convertedAt || '',
          'Owner': item.owner || ''
        }));
      }

      exportToCSV(formattedData, pageName, { title, type }, headers);
      showNotification(`${title} exported successfully`);
      setSelectedRows(new Set());
    } catch (error) {

      showNotification('Failed to export', 'error');
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const type = 'All Data';
      const pageName = activeTab === 'convert' ? 'qualified_leads_all' : 'converted_opportunities_all';

      let result;
      if (activeTab === 'convert') {
        const queryParams = new URLSearchParams({ stage: 'qualified', type });
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/crm/leads/export?${queryParams.toString()}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const blob = await response.blob();
          result = { success: true, data: blob };
        } else {
          result = { success: false, error: 'Failed to export' };
        }
      } else {
        const queryParams = new URLSearchParams({ type });
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/crm/opportunities/export?${queryParams.toString()}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const blob = await response.blob();
          result = { success: true, data: blob };
        } else {
          result = { success: false, error: 'Failed to export' };
        }
      }

      if (result.success && result.data) {
        const filename = generateExportFilename(pageName);
        const url = window.URL.createObjectURL(result.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showNotification('Full export completed successfully');
        setShowExportModal(false);
      } else {
        showNotification(result.error || 'Failed to export', 'error');
      }
    } catch (error) {

      showNotification('Failed to export', 'error');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const type = `Date Range (${startDate} to ${endDate})`;
      const pageName = activeTab === 'convert' ? 'qualified_leads_range' : 'converted_opportunities_range';

      let result;
      if (activeTab === 'convert') {
        const queryParams = new URLSearchParams({ stage: 'qualified', startDate, endDate, type });
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/crm/leads/export?${queryParams.toString()}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const blob = await response.blob();
          result = { success: true, data: blob };
        } else {
          result = { success: false, error: 'Failed to export' };
        }
      } else {
        const queryParams = new URLSearchParams({ startDate, endDate, type });
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/crm/opportunities/export?${queryParams.toString()}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const blob = await response.blob();
          result = { success: true, data: blob };
        } else {
          result = { success: false, error: 'Failed to export' };
        }
      }

      if (result.success && result.data) {
        const filename = generateExportFilename(pageName);
        const url = window.URL.createObjectURL(result.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showNotification('Date range export completed successfully');
        setShowExportModal(false);
      } else {
        showNotification(result.error || 'Failed to export', 'error');
      }
    } catch (error) {

      showNotification('Failed to export', 'error');
    } finally {
      setExportLoading(false);
    }
  };



  // Notification helper
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Fetch qualified leads for conversion
  const fetchQualifiedLeads = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {
        search: leadsFilters.search,
        page: leadsFilters.page,
        limit: leadsFilters.limit
      };

      // Add source filter if not 'all'
      if (leadsFilters.source && leadsFilters.source !== 'all') {
        filters.source = leadsFilters.source;
      }

      // Add temperature filter if not 'all'
      if (leadsFilters.temperature && leadsFilters.temperature !== 'all') {
        filters.scoreLabel = leadsFilters.temperature.toLowerCase();
      }

      // Add date range filters
      if (leadsFilters.startDate) {
        filters.startDate = leadsFilters.startDate;
      }
      if (leadsFilters.endDate) {
        filters.endDate = leadsFilters.endDate;
      }

      const result = await leadService.getQualifiedLeadsForConversion(filters);

      if (result.success) {
        // Map leads to the format needed for the list view
        const mappedLeads = result.leads.map(lead => ({
          id: lead._id || lead.id,
          _id: lead._id,
          leadId: lead.leadId,
          firstName: lead.firstName,
          lastName: lead.lastName,
          name: getLeadName(lead),
          company: lead.company || 'No company',
          email: lead.email || 'No email',
          phone: lead.phone || 'No phone',
          source: (() => {
            if (!lead.source) return 'Manual Entry';
            if (typeof lead.source === 'object' && lead.source.name) return lead.source.name;
            const foundSource = leadSources.find(s => s._id === lead.source || s.id === lead.source || s.name === lead.source);
            return foundSource ? foundSource.name : typeof lead.source === 'string' ? lead.source : 'Manual Entry';
          })(),
          score: lead.score || 0,
          scoreLabel: lead.scoreLabel ? lead.scoreLabel.charAt(0).toUpperCase() + lead.scoreLabel.slice(1) : 'Cold',
          region: lead.region || 'N/A',
          leadValue: lead.leadValue ? Number(lead.leadValue) : 0,
          qualification: lead.stage || 'Open',
          lastActivity: formatDate(lead.updatedAt || lead.createdAt),
          createdAt: lead.createdAt || lead.createdAtDate || new Date(),
          // Assignee/assignedTo mapping (may be string or object)
          assignee: lead.assignedTo && typeof lead.assignedTo === 'object'
            ? `${lead.assignedTo.firstName || ''} ${lead.assignedTo.lastName || ''}`.trim() || lead.assignedTo.email
            : (lead.assignedTo || 'Unassigned'),
          // Keep owner for backward compatibility
          owner: lead.assignedTo && typeof lead.assignedTo === 'object'
            ? `${lead.assignedTo.firstName || ''} ${lead.assignedTo.lastName || ''}`.trim() || lead.assignedTo.email
            : (lead.assignedTo || 'Unassigned'),
          // Human readable status from stage
          status: (() => {
            if (lead.stage === 'closed_won') return 'Won';
            if (lead.stage === 'closed_lost') return 'Lost';
            return lead.stage
              ? lead.stage.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
              : 'Open';
          })(),
          description: lead.notes || 'No description available',
          notes: lead.internalNotes || lead.notes || 'No notes available',
          budget: lead.budget || 0,
          timeline: lead.timeline || 'Not specified',
          decisionMaker: lead.designation || 'Not specified',
          painPoints: [],
          requirements: []
        }));

        setLeadsForConversion(mappedLeads);
        // Handle pagination data
        const paginationData = result.pagination || {};
        setLeadsPagination({
          currentPage: paginationData.currentPage || 1,
          totalPages: paginationData.totalPages || 1,
          totalItems: paginationData.totalLeads || mappedLeads.length,
          hasNextPage: paginationData.hasNextPage || false,
          hasPrevPage: paginationData.hasPrevPage || false,
          limit: paginationData.limit || leadsFilters.limit
        });

        // Update total qualified count from backend specific key if available
        if (result.totalQualifiedCount !== undefined) {
          setTotalQualifiedCount(result.totalQualifiedCount);
        } else {
          // Fallback: Check if we have active filters using localized logic to avoid scope issues
          const hasActiveFilters = leadsFilters.search.trim() !== '' || leadsFilters.source !== 'all';
          if (!hasActiveFilters) {
            setTotalQualifiedCount(paginationData.totalLeads || mappedLeads.length || 0);
          }
        }
      } else {

        showNotification(result.error || 'Failed to load qualified leads', 'error');
        setLeadsForConversion([]);
        setLeadsPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          hasNextPage: false,
          hasPrevPage: false,
          limit: leadsFilters.limit
        });
      }
    } catch (error) {

      showNotification('Failed to load qualified leads. Please try again.', 'error');
      setLeadsForConversion([]);
      setLeadsPagination({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        hasNextPage: false,
        hasPrevPage: false,
        limit: leadsFilters.limit
      });
    } finally {
      setLoading(false);
    }
  }, [leadsFilters.search, leadsFilters.source, leadsFilters.temperature, leadsFilters.startDate, leadsFilters.endDate, leadsFilters.page, leadsFilters.limit, leadSources]);

  // Fetch on mount and when filters/pagination change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchQualifiedLeads();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [fetchQualifiedLeads]);

  // Fetch converted opportunities
  const fetchConvertedOpportunities = useCallback(async () => {
    setLoadingOpportunities(true);
    try {
      const filters = {
        search: oppsFilters.oppSearch,
        page: oppsFilters.page,
        limit: oppsFilters.limit
      };

      // Add stage filter if not 'all'
      if (oppsFilters.status && oppsFilters.status !== 'all') {
        filters.stage = oppsFilters.status;
      }

      // Add date range filters
      if (oppsFilters.startDate) {
        filters.startDate = oppsFilters.startDate;
      }
      if (oppsFilters.endDate) {
        filters.endDate = oppsFilters.endDate;
      }

      const result = await leadService.getConvertedLeads(filters);

      if (result.success) {
        // Map opportunities to the format needed for display
        const mappedOpportunities = result.leads.map(lead => {
          const leadName = `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || lead.name || 'N/A';
          const companyName = lead.company || 'N/A';
          const oppName = lead.opportunityName || `${companyName} - Opportunity`;
          const oppValue = lead.opportunityValue || 0;
          const oppExpectedCloseDate = lead.opportunityExpectedCloseDate;
          const oppDescription = lead.opportunityDescription || '';

          // Format expected close date
          let expectedCloseFormatted = 'N/A';
          if (oppExpectedCloseDate) {
            const closeDate = new Date(oppExpectedCloseDate);
            expectedCloseFormatted = closeDate.toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            });
          }

          // Format conversion date (createdAt)
          let convertedAtFormatted = 'N/A';
          if (lead.conversionDate) {
            const convertedDate = new Date(lead.conversionDate);
            convertedAtFormatted = convertedDate.toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            });
          }

          // Format stage for display
          const stageDisplay = (() => {
            if (lead.stage === 'closed_won') return 'Won';
            if (lead.stage === 'closed_lost') return 'Lost';
            if (lead.stage === 'converted') return 'Converted';
            return lead.stage
              ? lead.stage.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
              : 'Converted';
          })();

          return {
            id: lead._id || lead.id,
            _id: lead._id, // MongoDB ObjectId
            opportunityId: lead.leadId,
            opportunityName: oppName,
            leadName: leadName,
            company: companyName,
            stage: stageDisplay,
            stageValue: lead.stage || 'converted',
            value: oppValue,
            description: oppDescription,
            opportunityDescription: oppDescription,
            probability: 0,
            expectedClose: expectedCloseFormatted,
            expectedCloseDate: oppExpectedCloseDate,
            owner: lead.assignedTo
              ? `${lead.assignedTo.firstName || ''} ${lead.assignedTo.lastName || ''}`.trim() || lead.assignedTo.email || 'Unknown'
              : 'Unknown',
            convertedAt: convertedAtFormatted,
            convertedAtDate: lead.conversionDate || lead.createdAt,
            convertedBy: lead.convertedBy,
            convertedByName: lead.convertedBy
              ? `${lead.convertedBy.firstName || ''} ${lead.convertedBy.lastName || ''}`.trim() || lead.convertedBy.email || 'Unknown'
              : 'Unknown',
            createdAt: lead.createdAt || new Date(),
            lastActivity: convertedAtFormatted,
            description: oppDescription,
            leadId: lead, // Store the full lead object, not just the ID
            // Quotation flags from backend
            hasQuotation: false,
            quotationId: null,
            quotationNumber: null,
            quotationStatus: null
          };
        });

        // Map pagination data correctly
        const paginationData = result.pagination || {};
        setConvertedOpportunities(mappedOpportunities);
        setOpportunityPagination({
          currentPage: paginationData.currentPage || 1,
          totalPages: paginationData.totalPages || 1,
          totalItems: paginationData.totalLeads || paginationData.totalItems || mappedOpportunities.length,
          hasNextPage: paginationData.hasNextPage || false,
          hasPrevPage: paginationData.hasPrevPage || false,
          limit: paginationData.limit || oppsFilters.limit
        });

        // Update total converted count from backend specific key if available
        if (result.totalConvertedCount !== undefined) {
          setTotalConvertedCount(result.totalConvertedCount);
        } else {
          // Fallback: Check if we have active filters using localized logic to avoid scope issues
          const hasActiveFilters = oppsFilters.oppSearch.trim() !== '' || oppsFilters.status !== 'all';
          if (!hasActiveFilters) {
            setTotalConvertedCount(paginationData.totalLeads || paginationData.totalItems || mappedOpportunities.length || 0);
          }
        }
      } else {

        showNotification(result.error || 'Failed to load converted opportunities', 'error');
        setConvertedOpportunities([]);
        setOpportunityPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          hasNextPage: false,
          hasPrevPage: false,
          limit: oppsFilters.limit
        });
      }
    } catch (error) {

      showNotification('Failed to load converted opportunities. Please try again.', 'error');
      setConvertedOpportunities([]);
      setOpportunityPagination({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        hasNextPage: false,
        hasPrevPage: false,
        limit: oppsFilters.limit
      });
    } finally {
      setLoadingOpportunities(false);
    }
  }, [oppsFilters.oppSearch, oppsFilters.status, oppsFilters.startDate, oppsFilters.endDate, oppsFilters.page, oppsFilters.limit]);

  // Fetch converted opportunities on mount and when filters/pagination change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchConvertedOpportunities();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [fetchConvertedOpportunities]);

  // Track previous tab to prevent duplicate calls
  const prevTabRef = useRef(null);
  
  // Fetch when tab changes
  useEffect(() => {
    if (prevTabRef.current !== activeTab) {
      prevTabRef.current = activeTab;
      
      if (activeTab === 'convert') {
        const timeoutId = setTimeout(() => {
          fetchQualifiedLeads();
        }, 500);
        return () => clearTimeout(timeoutId);
      } else if (activeTab === 'converted') {
        const timeoutId = setTimeout(() => {
          fetchConvertedOpportunities();
        }, 500);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [activeTab, fetchQualifiedLeads, fetchConvertedOpportunities]);

  const getScoreColor = (score) => {
    switch (score) {
      case 'Hot': return 'bg-red-100 text-red-800';
      case 'Warm': return 'bg-yellow-100 text-yellow-800';
      case 'Cold': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getQualificationColor = (qualification) => {
    switch (qualification) {
      case 'Highly Qualified': return 'bg-green-100 text-green-800';
      case 'Qualified': return 'bg-blue-100 text-blue-800';
      case 'Disqualified': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'Open': return 'bg-blue-100 text-blue-800';
      case 'Converted': return 'bg-blue-100 text-blue-800';
      case 'Contacted': return 'bg-green-100 text-green-800';
      case 'Qualified': return 'bg-purple-100 text-purple-800';
      case 'Proposal Sent': return 'bg-orange-100 text-orange-800';
      case 'Negotiation': return 'bg-yellow-100 text-yellow-800';
      case 'Won':
      case 'Closed Won': return 'bg-emerald-100 text-emerald-800';
      case 'Lost':
      case 'Closed Lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleConvertLead = (lead) => {
    setSelectedLead(lead);
    setShowConvertModal(true);
  };

  const handleConvertToOpportunity = async (lead, opportunityData) => {
    try {
      // Get lead ID (could be _id or leadId)
      const leadId = lead._id || lead.id || lead.leadId;

      if (!leadId) {
        throw new Error('Lead ID not found');
      }



      // Call API to convert lead to opportunity
      const result = await leadService.convertToOpportunity(leadId, opportunityData);

      if (result.success) {
        // Show success notification
        const leadName = lead.firstName && lead.lastName
          ? `${lead.firstName} ${lead.lastName}`
          : lead.name || lead.firstName || 'Lead';
        const companyName = lead.company || 'N/A';

        showNotification(
          `Lead "${leadName}" from ${companyName} converted to opportunity successfully!`,
          'success'
        );

        // Close modal
        setSelectedLead(null);
        setShowConvertModal(false);

        // Refresh the qualified leads list (the converted lead should no longer appear)
        await fetchQualifiedLeads();

        // Refresh converted opportunities list to show the new one
        await fetchConvertedOpportunities();

        // Switch to converted opportunities tab to show the new opportunity
        setActiveTab('converted');
      } else {
        throw new Error(result.error || 'Failed to convert lead to opportunity');
      }
    } catch (error) {


      // Extract error message
      let errorMessage = 'Failed to convert lead to opportunity. Please try again.';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.responseMessage) {
        errorMessage = error.response.data.responseMessage;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      showNotification(errorMessage, 'error');
      throw error; // Re-throw to let the modal know it failed
    }
  };

  const handleViewLead = (leadId) => {
    navigate(`/lead_detail_360/${leadId}`);
  };

  const handleViewOpportunity = (opportunityId) => {

    const opportunity = convertedOpportunities.find(opp => opp.id === opportunityId);
    if (opportunity) {
      setSelectedOpportunity(opportunity);
      setShowViewModal(true);
    }
  };

  const handleEditOpportunity = (opportunityId) => {

    const opportunity = convertedOpportunities.find(opp => opp.id === opportunityId);
    if (opportunity) {
      setSelectedOpportunity(opportunity);
      setShowEditModal(true);
    }
  };

  const handleDeleteOpportunity = (opportunityId) => {
    // Set the opportunity to delete and show confirmation modal
    setOpportunityToDelete(opportunityId);
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteOpportunity = async () => {
    if (!opportunityToDelete) return;

    try {
      const result = await leadService.deleteLead(opportunityToDelete);

      if (result.success) {
        // Remove the opportunity from the local state
        setConvertedOpportunities(prev =>
          prev.filter(opp => opp.id !== opportunityToDelete)
        );
        showNotification('Opportunity deleted successfully', 'success');
      } else {
        showNotification(result.error || 'Failed to delete opportunity', 'error');
      }
    } catch (error) {

      showNotification('Failed to delete opportunity', 'error');
    } finally {
      // Close the modal and reset state
      setShowDeleteConfirmModal(false);
      setOpportunityToDelete(null);
    }
  };

  const cancelDeleteOpportunity = () => {
    // Close the modal and reset state
    setShowDeleteConfirmModal(false);
    setOpportunityToDelete(null);
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      const selectedIds = Array.from(selectedRows);
      let successCount = 0;
      let lastError = null;

      if (activeTab === 'convert') {
        // Bulk delete leads
        const result = await leadService.bulkDeleteLeads(selectedIds);
        if (result.success) {
          successCount = selectedIds.length;
          setLeadsForConversion(prev => prev.filter(lead => !selectedRows.has(lead._id || lead.id)));
        } else {
          lastError = result.error;
        }
      } else {
        // Bulk delete opportunities
        const oppsToDelete = convertedOpportunities.filter(opp =>
          selectedRows.has(opp._id || opp.id)
        );

        const idsToDelete = oppsToDelete.map(opp => opp._id || opp.id);

        for (const id of idsToDelete) {
          const result = await leadService.deleteLead(id);
          if (result.success) {
            successCount++;
          } else {
            lastError = result.error;
          }
        }

        if (successCount > 0) {
          setConvertedOpportunities(prev => prev.filter(opp => !idsToDelete.includes(opp._id || opp.id)));
        }

        if (successCount < selectedIds.length) {
          lastError = "Some opportunities could not be deleted.";
        }
      }

      if (successCount > 0) {
        showNotification(`Successfully deleted ${successCount} ${activeTab === 'convert' ? 'leads' : 'opportunities'}!`, 'success');
        setSelectedRows(new Set());
        if (activeTab === 'convert') {
          fetchQualifiedLeads();
        } else {
          fetchConvertedOpportunities();
        }
      }

      if (lastError) {
        showNotification(lastError || 'Some items could not be deleted', 'error');
      }
      setShowBulkDeleteModal(false);
    } catch (error) {

      showNotification('An error occurred during bulk deletion', 'error');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleCreateQuotation = (opportunity) => {
    // Use MongoDB _id if available, otherwise use opportunityId
    const oppId = opportunity._id || opportunity.id;
    navigate(`/crm/quotation-proposal/quote-builder?opportunityId=${oppId}`);
  };

  const handleViewQuotation = (opportunity) => {
    // Navigate to Send & Track page with quotation ID
    const quoteId = opportunity.quotationId || opportunity.quotationNumber;
    if (quoteId) {
      navigate(`/crm/quotation-proposal/send-track/${quoteId}`);
    } else {
      showNotification('Quotation ID not found', 'error');
    }
  };

  const handleSaveOpportunity = async (updatedData) => {
    if (!selectedOpportunity) return;
    setIsSaving(true);

    try {
      // Make actual API call to update opportunity
      const result = await leadService.updateLead(selectedOpportunity._id || selectedOpportunity.id, updatedData);

      if (result.success) {
        // Refresh converted opportunities list to show changes
        await fetchConvertedOpportunities();

        showNotification('Opportunity updated successfully', 'success');
        setShowEditModal(false);
        setSelectedOpportunity(null);
      } else {
        throw new Error(result.error || 'Failed to update opportunity');
      }
    } catch (error) {

      let errorMessage = 'Failed to update opportunity. Please try again.';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.responseMessage) {
        errorMessage = error.response.data.responseMessage;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      showNotification(errorMessage, 'error');
    } finally {
      setIsSaving(false);
    }
  };






  const handleCreateQuote = (leadId) => {
    // Navigate to quote builder with lead ID
    navigate(`/quote_builder?leadId=${leadId}`);
  };

  const handleExportLeads = () => {
    // Create CSV content for leads ready for conversion
    const csvContent = leadsForConversion.map(lead =>
      `${lead.name},${lead.email},${lead.company},${lead.score},${lead.qualification}`
    ).join('\n');

    const blob = new Blob([`Name,Email,Company,Score,Qualification\n${csvContent}`], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `qualified_leads_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportOpportunities = () => {
    // Create CSV content for converted opportunities
    const csvContent = convertedOpportunities.map(opp =>
      `${opp.name},${opp.leadName},${opp.value},${opp.stage},${opp.probability}`
    ).join('\n');

    const blob = new Blob([`Opportunity,Lead,Value,Stage,Probability\n${csvContent}`], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `opportunities_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Handle items per page change for leads
  const handleLeadsItemsPerPageChange = (newLimit) => {
    updateLeadsLimit(newLimit);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle page change for leads
  const handleLeadsPageChange = (page) => {
    updateLeadsPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle items per page change for opportunities
  const handleOpportunitiesItemsPerPageChange = (newLimit) => {
    updateOppsLimit(newLimit);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle page change for opportunities
  const handleOpportunitiesPageChange = (page) => {
    updateOppsPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Aliases for backward compatibility
  const handleItemsPerPageChange = handleOpportunitiesItemsPerPageChange;
  const handlePageChange = handleOpportunitiesPageChange;

  // Check if there are active filters for qualified leads
  const hasActiveLeadsFilters = useMemo(() => {
    return (
      leadsFilters.search.trim() !== '' ||
      leadsFilters.source !== 'all' ||
      leadsFilters.temperature !== 'all' ||
      leadsFilters.startDate !== '' ||
      leadsFilters.endDate !== ''
    );
  }, [leadsFilters.search, leadsFilters.source, leadsFilters.temperature, leadsFilters.startDate, leadsFilters.endDate]);

  // Check if there are active filters for converted opportunities
  const hasActiveOpportunitiesFilters = useMemo(() => {
    return (
      oppsFilters.oppSearch.trim() !== '' ||
      oppsFilters.status !== 'all' ||
      oppsFilters.startDate !== '' ||
      oppsFilters.endDate !== ''
    );
  }, [oppsFilters.oppSearch, oppsFilters.status, oppsFilters.startDate, oppsFilters.endDate]);

  // Clear filters for qualified leads
  const handleClearLeadsFilters = () => {
    resetLeadsFilters();
  };

  // Clear filters for converted opportunities
  const handleClearOpportunitiesFilters = () => {
    resetOppsFilters();
  };

  return (
    <div className="space-y-6 min-h-0">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg max-w-sm ${notification.type === 'success'
          ? 'bg-green-500 text-white'
          : notification.type === 'error'
            ? 'bg-red-500 text-white'
            : notification.type === 'info'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-500 text-white'
          }`}>
          <div className="flex items-center">
            <div className="flex-1">
              <div className="text-sm font-medium">{notification.message}</div>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="ml-4 text-white hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center gap-4">
          <div className="bg-slate-900 p-2.5 rounded-xl shadow-sm border border-slate-800 flex items-center justify-center">
            <ArrowRight className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">Convert to Opportunity</h1>
            <p className="text-sm sm:text-base text-slate-500 mt-1">Convert qualified leads into sales opportunities</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">


          <button
            className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
            onClick={handleExportClick}
          >
            <Download className="h-4 w-4" />
            <span>{selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}</span>
          </button>

          {selectedRows.size > 0 && (
            <PermissionGate permission="lead_management" action="delete">
              <button
                className="flex items-center justify-center space-x-1.5 !bg-red-600 hover:!bg-red-700 !text-white rounded-md px-3 py-1.5 text-[13px] font-medium h-8 shadow-sm transition-colors"
                onClick={handleBulkDelete}
                disabled={isBulkDeleting}
              >
                {isBulkDeleting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                <span>{isBulkDeleting ? 'Deleting...' : `Delete (${selectedRows.size})`}</span>
              </button>
            </PermissionGate>
          )}

          <PermissionGate permission="lead_management" action="read">
            <button
              className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
              onClick={() => navigate('/lead_list_filters')}
            >
              <ArrowRight className="h-4 w-4" />
              <span>View All Leads</span>
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { key: 'convert', label: 'Qualified Leads', count: totalQualifiedCount, permission: 'lead_management' },
              { key: 'converted', label: 'Converted Opportunities', count: totalConvertedCount, permission: 'lead_management' }
            ].map((tab) => (
              <PermissionGate key={tab.key} permission={tab.permission} action="read">
                <button
                  onClick={() => setActiveTab(tab.key)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.key
                    ? 'border-brand text-brand'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  {tab.label}
                  <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${activeTab === tab.key
                    ? 'bg-brand text-white border-brand'
                    : 'bg-gray-100 text-gray-600'
                    }`}>
                    {tab.count}
                  </span>
                </button>
              </PermissionGate>
            ))}
          </nav>
        </div>

        <div className="p-6 overflow-x-auto">
          {activeTab === 'convert' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="relative flex-1 w-full sm:w-auto sm:min-w-[280px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      const sanitized = handleSearchInputChange(e, setLeadsSearchTerm, FIELD_LIMITS.search);
                      setLeadsSearchTerm(sanitized);
                    }}
                    placeholder="Search by ID, name, email, company..."
                    className="input-field pl-10"
                    maxLength={FIELD_LIMITS.search}
                    autoFocus
                  />
                </div>
                <select
                  value={leadsFilters.source}
                  onChange={(e) => updateLeadsFilter({ source: e.target.value })}
                  className="input-field w-full sm:w-auto"
                >
                  <option value="all">All Sources</option>
                  {leadSources.map(source => (
                    <option key={source._id || source.id || source.name} value={source._id || source.id || source.name}>
                      {source.name}
                    </option>
                  ))}
                </select>
                <select
                  value={leadsFilters.temperature}
                  onChange={(e) => updateLeadsFilter({ temperature: e.target.value })}
                  className="input-field w-full sm:w-auto"
                >
                  <option value="all">All Temperatures</option>
                  <option value="Hot">Hot</option>
                  <option value="Warm">Warm</option>
                  <option value="Cold">Cold</option>
                </select>
                <div className="flex-shrink-0">
                  <DateRangeFilter
                    startDate={leadsFilters.startDate}
                    endDate={leadsFilters.endDate}
                    onChange={(range) => updateLeadsFilter({ startDate: range.startDate, endDate: range.endDate })}
                  />
                </div>
                {hasActiveLeadsFilters && (
                  <PermissionGate permission="lead_management" action="read">
                    <button
                      className="btn-cancel flex items-center justify-center space-x-2"
                      onClick={handleClearLeadsFilters}
                    >
                      <X className="h-4 w-4" />
                      <span>Clear</span>
                    </button>
                  </PermissionGate>
                )}
              </div>

              <div className="space-y-4">
                {loading ? (
                  <div className="text-center py-12">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F2942]"></div>
                      <span className="ml-2 text-gray-600">Loading leads...</span>
                    </div>
                  </div>
                ) : leadsForConversion.length === 0 ? (
                  <div className="text-center py-12">
                    <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Qualified Leads Ready for Conversion</h3>
                    <p className="text-gray-600 mb-4">You don't have any qualified leads ready to convert to opportunities yet.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-5 py-3 text-left">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-brand"
                              checked={leadsForConversion.length > 0 && leadsForConversion.every(item => selectedRows.has(item._id || item.id))}
                              onChange={() => handleSelectAll(leadsForConversion)}
                            />
                          </th>
                          <th
                            className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleLeadsSort('leadId')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Lead ID</span>
                              {leadsSortColumn === 'leadId' ? (
                                leadsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[220px] cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleLeadsSort('leadDetails')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Lead Details</span>
                              {leadsSortColumn === 'leadDetails' ? (
                                leadsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleLeadsSort('source')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Source</span>
                              {leadsSortColumn === 'source' ? (
                                leadsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleLeadsSort('score')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Score</span>
                              {leadsSortColumn === 'score' ? (
                                leadsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleLeadsSort('temperature')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Temperature</span>
                              {leadsSortColumn === 'temperature' ? (
                                leadsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleLeadsSort('status')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Status</span>
                              {leadsSortColumn === 'status' ? (
                                leadsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleLeadsSort('owner')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Owner</span>
                              {leadsSortColumn === 'owner' ? (
                                leadsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleLeadsSort('lastActivity')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Last Activity</span>
                              {leadsSortColumn === 'lastActivity' ? (
                                leadsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleLeadsSort('createdAt')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Created At</span>
                              {leadsSortColumn === 'createdAt' ? (
                                leadsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {getSortedLeadsForConversion().map((lead) => (
                          <tr key={lead.id} className="hover:bg-gray-50">
                            <td className="px-5 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-brand"
                                checked={selectedRows.has(lead._id || lead.id)}
                                onChange={() => handleSelectRow(lead._id || lead.id)}
                              />
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-900 font-medium font-mono">
                              {lead.leadId || 'No ID'}
                            </td>
                            <td className="px-5 py-4 max-w-[240px]">
                              <div className="flex items-center space-x-3 min-w-0">
                                <div className="min-w-0 flex-1 overflow-hidden">
                                  <div className="text-sm font-medium text-gray-900 truncate capitalize" title={lead.name || `${lead.firstName || ''} ${lead.lastName || ''}`}>{lead.name || `${lead.firstName || ''} ${lead.lastName || ''}`}</div>
                                  <div className="text-sm text-gray-500 truncate first-letter:capitalize" title={lead.company || 'No company'}>{lead.company || 'No company'}</div>
                                  <div className="text-xs text-gray-400 truncate" title={lead.email || 'No email'}>{lead.email || 'No email'}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-900">{mapSourceToFrontend(lead.source)}</td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-900">
                              {lead.score}
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getScoreColor(lead.scoreLabel)}`}>
                                {lead.scoreLabel}
                              </span>
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStageColor(lead.status)}`}>{lead.status}</span>
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-900">{lead.assignee || lead.owner || 'Unassigned'}</td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm">
                              <div className="text-gray-900">{lead.lastActivity?.date || lead.lastActivity}</div>
                              {lead.lastActivity?.time && <div className="text-xs text-gray-500">{lead.lastActivity.time}</div>}
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm">
                              <div className="text-gray-900">{formatDate(lead.createdAt).date}</div>
                              <div className="text-xs text-gray-500">{formatDate(lead.createdAt).time}</div>
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <PermissionGate permission="lead_management" action="read">
                                  <button
                                    onClick={() => handleViewLead(lead.id)}
                                    className="text-[#0F2942] hover:text-blue-900"
                                    title="View Lead"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                </PermissionGate>
                                <PermissionGate permission="lead_management" action="create">
                                  <button
                                    className="btn-create flex items-center justify-center space-x-2 !py-1 !px-3 text-xs"
                                    onClick={() => handleConvertLead(lead)}
                                  >
                                    <ArrowRight className="h-4 w-4" />
                                    <span>Convert</span>
                                  </button>
                                </PermissionGate>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Pagination for Qualified Leads */}
              {(() => {
                const leadsTotalItems = Number(leadsPagination?.totalItems || leadsForConversion.length || 0);
                const leadsTotalPages = Number(leadsPagination?.totalPages || 1);
                const effectivePaginationLimit = leadsPagination?.limit || leadsFilters.limit || 10;
                // Show pagination if there's more than one page
                const shouldShowPagination = !loading && leadsTotalPages > 1;

                return shouldShowPagination ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                    <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                      {/* Results Count and Limit Selector */}
                      <div className="flex items-center space-x-4">
                        <div className="text-sm text-gray-700">
                          Showing {((leadsPagination.currentPage || 1) - 1) * effectivePaginationLimit + 1} to {Math.min((leadsPagination.currentPage || 1) * effectivePaginationLimit, leadsTotalItems)} of {leadsTotalItems} results
                        </div>
                        <div className="flex items-center space-x-2">
                          <label className="text-sm text-gray-700">Items per page:</label>
                          <select
                            value={leadsFilters.limit}
                            onChange={(e) => handleLeadsItemsPerPageChange(Number(e.target.value))}
                            className="input-field !w-auto !py-1 !px-2 text-sm"
                          >
                            <option value={3}>3</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>
                      </div>

                      {/* Pagination Controls */}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleLeadsPageChange((leadsPagination.currentPage || 1) - 1)}
                          disabled={leadsPagination.hasPrevPage === false || (leadsPagination.currentPage || 1) <= 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Previous
                        </button>

                        {/* Page Numbers */}
                        <div className="flex items-center space-x-1">
                          {(() => {
                            const totalPages = leadsPagination.totalPages || 1;
                            const currentPageNum = leadsPagination.currentPage || 1;
                            const pages = [];

                            if (totalPages <= 10) {
                              for (let i = 1; i <= totalPages; i++) {
                                pages.push(i);
                              }
                            } else {
                              pages.push(1);

                              if (currentPageNum <= 4) {
                                for (let i = 2; i <= 5; i++) {
                                  pages.push(i);
                                }
                                pages.push('ellipsis');
                                pages.push(totalPages);
                              } else if (currentPageNum >= totalPages - 3) {
                                pages.push('ellipsis');
                                for (let i = totalPages - 4; i <= totalPages; i++) {
                                  pages.push(i);
                                }
                              } else {
                                pages.push('ellipsis');
                                for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
                                  pages.push(i);
                                }
                                pages.push('ellipsis');
                                pages.push(totalPages);
                              }
                            }

                            return pages.map((page, index) => {
                              if (page === 'ellipsis') {
                                return (
                                  <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                                    ...
                                  </span>
                                );
                              }

                              return (
                                <button
                                  key={page}
                                  onClick={() => handleLeadsPageChange(page)}
                                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                                    ? 'bg-brand text-white border-brand'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                  {page}
                                </button>
                              );
                            });
                          })()}
                        </div>

                        <button
                          onClick={() => handleLeadsPageChange((leadsPagination.currentPage || 1) + 1)}
                          disabled={leadsPagination.hasNextPage === false || (leadsPagination.currentPage || 1) >= (leadsPagination.totalPages || 1)}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          )}

          {activeTab === 'converted' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="relative flex-1 w-full sm:w-auto sm:min-w-[280px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={oppsSearchTerm}
                    placeholder="Search by ID, name, lead name, company..."
                    onChange={(e) => {
                      const sanitized = handleSearchInputChange(e, setOppsSearchTerm, FIELD_LIMITS.search);
                      setOppsSearchTerm(sanitized);
                    }}
                    className="input-field pl-10"
                    maxLength={FIELD_LIMITS.search}
                  />
                </div>
                <select
                  value={oppsFilters.status}
                  onChange={(e) => updateOppsFilter({ status: e.target.value })}
                  className="input-field w-full sm:w-auto"
                >
                  <option value="all">All Status</option>
                  {leadStatuses.filter(s => s.isActive).map(status => (
                    <option key={status.value} value={status.value}>
                      {status.name}
                    </option>
                  ))}
                </select>
                <div className="flex-shrink-0">
                  <DateRangeFilter
                    startDate={oppsFilters.startDate}
                    endDate={oppsFilters.endDate}
                    onChange={(range) => updateOppsFilter({ startDate: range.startDate, endDate: range.endDate })}
                  />
                </div>
                {hasActiveOpportunitiesFilters && (
                  <PermissionGate permission="lead_management" action="read">
                    <button
                      className="btn-cancel flex items-center justify-center space-x-2"
                      onClick={handleClearOpportunitiesFilters}
                    >
                      <X className="h-4 w-4" />
                      <span>Clear</span>
                    </button>
                  </PermissionGate>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 table-fixed">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left w-12">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-brand"
                          checked={convertedOpportunities.length > 0 && convertedOpportunities.every(item => selectedRows.has(item._id || item.id))}
                          onChange={() => handleSelectAll(convertedOpportunities)}
                        />
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleOppsSort('opportunity')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Opportunity</span>
                          {oppsSortColumn === 'opportunity' ? (
                            oppsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleOppsSort('leadDetails')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Lead Details</span>
                          {oppsSortColumn === 'leadDetails' ? (
                            oppsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleOppsSort('value')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Value</span>
                          {oppsSortColumn === 'value' ? (
                            oppsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleOppsSort('status')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Status</span>
                          {oppsSortColumn === 'status' ? (
                            oppsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleOppsSort('expectedClose')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Expected Close</span>
                          {oppsSortColumn === 'expectedClose' ? (
                            oppsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleOppsSort('convertedAtDate')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Converted At</span>
                          {oppsSortColumn === 'convertedAtDate' ? (
                            oppsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleOppsSort('createdAt')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Created At</span>
                          {oppsSortColumn === 'createdAt' ? (
                            oppsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loadingOpportunities ? (
                      <tr>
                        <td colSpan="8" className="px-6 py-12 text-center">
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F2942]"></div>
                            <span className="ml-2 text-gray-600">Loading converted opportunities...</span>
                          </div>
                        </td>
                      </tr>
                    ) : convertedOpportunities.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-6 py-12 text-center">
                          <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No Converted Opportunities</h3>
                          <p className="text-gray-600">No opportunities have been converted from leads yet.</p>
                        </td>
                      </tr>
                    ) : (
                      getSortedConvertedOpportunities().map((opportunity) => (
                        <tr key={opportunity.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-brand"
                              checked={selectedRows.has(opportunity._id || opportunity.id)}
                              onChange={() => handleSelectRow(opportunity._id || opportunity.id)}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap w-1/4">
                            <div>
                              <div className="text-sm font-medium text-gray-900 truncate capitalize" title={opportunity.opportunityName}>{truncateText(opportunity.opportunityName, 30)}</div>
                              <div className="text-xs text-gray-500 truncate" title={`ID: ${opportunity.opportunityId || 'N/A'}`}>
                                ID: {opportunity.opportunityId || 'N/A'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap w-1/4">
                            <div>
                              <div className="text-sm font-medium text-gray-900 truncate capitalize" title={opportunity.leadName}>{truncateText(opportunity.leadName, 25)}</div>
                              <div className="text-sm text-gray-500 truncate first-letter:capitalize" title={opportunity.company}>{truncateText(opportunity.company, 25)}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="truncate" title={`₹${typeof opportunity.value === 'number' ? formatIndianAmount(opportunity.value) : '0'}`}>
                              ₹{typeof opportunity.value === 'number' ? formatIndianAmount(opportunity.value) : '0'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStageColor(opportunity.stage)}`}>
                              {opportunity.stage}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="truncate" title={opportunity.expectedClose || 'N/A'}>
                              {opportunity.expectedClose || 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="text-gray-900">{formatDate(opportunity.convertedAtDate).date}</div>
                            <div className="text-xs text-gray-500">{formatDate(opportunity.convertedAtDate).time}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="text-gray-900">{formatDate(opportunity.createdAt).date}</div>
                            <div className="text-xs text-gray-500">{formatDate(opportunity.createdAt).time}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <PermissionGate permission="lead_management" action="read">
                                <button
                                  onClick={() => handleViewOpportunity(opportunity.id)}
                                  className="text-[#0F2942] hover:text-blue-900"
                                  title="View Opportunity"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                              </PermissionGate>
{/* Edit opportunity decommissioned */}
                              {/* <PermissionGate permission="lead_management" action="update">
                                <button
                                  onClick={() => handleEditOpportunity(opportunity.id)}
                                  className="text-green-600 hover:text-green-900"
                                  title="Edit Opportunity"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              </PermissionGate> */}
                              <PermissionGate permission="lead_management" action="delete">
                                <button
                                  onClick={() => handleDeleteOpportunity(opportunity.id)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete Opportunity"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {(() => {
                const opportunitiesTotalItems = Number(opportunityPagination?.totalItems || convertedOpportunities.length || 0);
                const opportunitiesTotalPages = Number(opportunityPagination?.totalPages || 1);
                const effectivePaginationLimit = opportunityPagination?.limit || oppsFilters.limit || 10;
                // Show pagination if there's more than one page
                const shouldShowPagination = !loadingOpportunities && opportunitiesTotalPages > 1;

                return shouldShowPagination ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                    <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                      {/* Results Count and Limit Selector */}
                      <div className="flex items-center space-x-4">
                        <div className="text-sm text-gray-700">
                          Showing {((opportunityPagination.currentPage || 1) - 1) * effectivePaginationLimit + 1} to {Math.min((opportunityPagination.currentPage || 1) * effectivePaginationLimit, opportunitiesTotalItems)} of {opportunitiesTotalItems} results
                        </div>
                        <div className="flex items-center space-x-2">
                          <label className="text-sm text-gray-700">Items per page:</label>
                          <select
                            value={oppsFilters.limit}
                            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                            className="input-field !w-auto !py-1 !px-2 text-sm"
                          >
                            <option value={3}>3</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>
                      </div>

                      {/* Pagination Controls */}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handlePageChange((opportunityPagination.currentPage || 1) - 1)}
                          disabled={opportunityPagination.hasPrevPage === false || (opportunityPagination.currentPage || 1) <= 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Previous
                        </button>

                        {/* Page Numbers */}
                        <div className="flex items-center space-x-1">
                          {(() => {
                            const totalPages = opportunityPagination.totalPages || 1;
                            const currentPageNum = opportunityPagination.currentPage || 1;
                            const pages = [];

                            if (totalPages <= 10) {
                              for (let i = 1; i <= totalPages; i++) {
                                pages.push(i);
                              }
                            } else {
                              pages.push(1);

                              if (currentPageNum <= 4) {
                                for (let i = 2; i <= 5; i++) {
                                  pages.push(i);
                                }
                                pages.push('ellipsis');
                                pages.push(totalPages);
                              } else if (currentPageNum >= totalPages - 3) {
                                pages.push('ellipsis');
                                for (let i = totalPages - 4; i <= totalPages; i++) {
                                  pages.push(i);
                                }
                              } else {
                                pages.push('ellipsis');
                                for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
                                  pages.push(i);
                                }
                                pages.push('ellipsis');
                                pages.push(totalPages);
                              }
                            }

                            return pages.map((page, index) => {
                              if (page === 'ellipsis') {
                                return (
                                  <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                                    ...
                                  </span>
                                );
                              }

                              return (
                                <button
                                  key={page}
                                  onClick={() => handlePageChange(page)}
                                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                                    ? 'bg-brand text-white border-brand'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                  {page}
                                </button>
                              );
                            });
                          })()}
                        </div>

                        <button
                          onClick={() => handlePageChange((opportunityPagination.currentPage || 1) + 1)}
                          disabled={opportunityPagination.hasNextPage === false || (opportunityPagination.currentPage || 1) >= (opportunityPagination.totalPages || 1)}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Convert Lead Modal */}
      <ConvertLeadModal
        isOpen={showConvertModal}
        onClose={() => setShowConvertModal(false)}
        lead={selectedLead}
        onSubmit={handleConvertToOpportunity}
      />

      {/* View Opportunity Modal */}
      {showViewModal && selectedOpportunity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Opportunity Details</h3>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">


                {/* Basic Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Basic Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Opportunity Name</p>
                      <p className="text-sm font-medium text-gray-900 break-words capitalize" title={selectedOpportunity.opportunityName}>{selectedOpportunity.opportunityName || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Lead Name</p>
                      <p className="text-sm font-medium text-gray-900 break-words capitalize" title={selectedOpportunity.leadName}>{selectedOpportunity.leadName || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Company</p>
                      <p className="text-sm font-medium text-gray-900 break-words first-letter:capitalize" title={selectedOpportunity.company}>{selectedOpportunity.company || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Opportunity Value</p>
                      <p className="text-sm font-medium text-gray-900 break-words" title={`₹${selectedOpportunity.value ? formatIndianAmount(selectedOpportunity.value) : '0'}`}>
                        ₹{selectedOpportunity.value ? formatIndianAmount(selectedOpportunity.value) : '0'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Expected Close</p>
                      <p className="text-sm font-medium text-gray-900 break-words" title={selectedOpportunity.expectedClose}>{selectedOpportunity.expectedClose || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="text-sm font-medium text-gray-900 break-words" title={selectedOpportunity.leadId?.email || 'N/A'}>{selectedOpportunity.leadId?.email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="text-sm font-medium text-gray-900 break-words" title={selectedOpportunity.leadId?.phone || 'N/A'}>{selectedOpportunity.leadId?.phone || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Additional Details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div>
                    <p className="text-sm text-gray-600">Description</p>
                    <p className="text-sm font-medium text-gray-900 break-words first-letter:capitalize" title={selectedOpportunity.description || 'No description'}>{selectedOpportunity.description || 'No description'}</p>
                  </div>
                </div>

                {/* Loss Reason History */}
                {opportunityLostReasons.length > 0 && (
                  <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                    <h4 className="text-sm font-bold text-red-800 mb-3 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      Loss Reason History
                    </h4>
                    <div className="space-y-3">
                      {opportunityLostReasons.map((lr, index) => {
                        const reasonText = typeof lr === 'object' ? lr.reason : lr;
                        const reasonDate = typeof lr === 'object' ? lr.date : null;

                        return (
                          <div key={index} className={`${index !== 0 ? 'border-t border-red-100 pt-3' : ''}`}>
                            <p className="text-sm text-gray-800 first-letter:capitalize">{reasonText}</p>
                            {reasonDate && (
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(reasonDate).toLocaleDateString('en-IN', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Timeline */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Timeline</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Converted At</p>
                      <p className="text-sm font-medium text-gray-900">{selectedOpportunity.convertedAt}</p>
                    </div>

                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  className="btn-cancel"
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </button>
{/* Edit opportunity decommissioned */}
                {/* <PermissionGate permission="lead_management" action="update">
                  <span title={selectedOpportunity.stage === 'Closed Won' || selectedOpportunity.stageValue === 'closed_won' ? "Closed Won opportunities cannot be edited" : ""}>
                    <button
                      className={`btn-create flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed ${selectedOpportunity.stage === 'Closed Won' || selectedOpportunity.stageValue === 'closed_won' ? '!bg-gray-400' : ''}`}
                      onClick={() => {
                        if (selectedOpportunity.stage !== 'Closed Won' && selectedOpportunity.stageValue !== 'closed_won') {
                          setShowViewModal(false);
                          handleEditOpportunity(selectedOpportunity.id);
                        }
                      }}
                      disabled={selectedOpportunity.stage === 'Closed Won' || selectedOpportunity.stageValue === 'closed_won'}
                    >
                      Edit Opportunity
                    </button>
                  </span>
                </PermissionGate> */}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* <OpportunityEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        opportunity={selectedOpportunity}
        onSave={handleSaveOpportunity}
        isSaving={isSaving}
      /> */}

      {/* Delete Opportunity Confirmation Modal */}
      {showDeleteConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
                <button
                  onClick={cancelDeleteOpportunity}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <p className="text-sm text-gray-700">
                  Are you sure you want to delete this opportunity? This action cannot be undone.
                </p>
              </div>

              {/* Modal Actions */}
              <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  className="btn-cancel"
                  onClick={cancelDeleteOpportunity}
                >
                  Cancel
                </button>
                <PermissionGate permission="lead_management" action="delete">
                  <button
                    className="btn-create !bg-red-600 hover:!bg-red-700"
                    onClick={confirmDeleteOpportunity}
                  >
                    Delete Opportunity
                  </button>
                </PermissionGate>
              </div>
            </div>
          </div>
        </div>
      )}

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExportCurrentPage={handleExportCurrentPage}
        onExportAll={handleExportAll}
        onExportRange={handleExportRange}
        loading={exportLoading}
        title={`Export ${activeTab === 'convert' ? 'Qualified Leads' : 'Converted Opportunities'}`}
        showDateRange={true}
      />

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[110] !mt-0 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-auto shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                Bulk Delete {activeTab === 'convert' ? 'Qualified Leads' : 'Converted Opportunities'}
              </h3>
              <button
                onClick={() => !isBulkDeleting && setShowBulkDeleteModal(false)}
                className="text-gray-400 hover:text-gray-600"
                disabled={isBulkDeleting}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600">
                Are you sure you want to delete <span className="font-bold text-gray-900">{selectedRows.size}</span> selected {activeTab === 'convert' ? 'leads' : 'opportunities'}? This action cannot be undone.
              </p>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <button
                className="btn-cancel"
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={isBulkDeleting}
              >
                Cancel
              </button>
              <button
                className="btn-create flex items-center justify-center space-x-2 !bg-red-600 hover:!bg-red-700"
                onClick={confirmBulkDelete}
                disabled={isBulkDeleting}
              >
                {isBulkDeleting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                <span>{isBulkDeleting ? 'Deleting...' : `Delete All Selected (${selectedRows.size})`}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConvertOpportunity;
